import React, { useEffect, useState } from "react";
import { Command } from "cmdk";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Briefcase, Lightbulb, Layers } from "lucide-react";
import { useContent } from "@/lib/content";
import { NAV } from "@/data/site";

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { services: SERVICES, cases: CASES, insights: INSIGHTS } = useContent();
  const [q, setQ] = useState("");

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(true);
      } else if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [onOpenChange]);

  const go = (path) => { onOpenChange(false); setTimeout(() => navigate(path), 50); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-[8vh] px-4" data-testid="command-palette">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => onOpenChange(false)} />
      <div className="relative bg-white w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden border border-black/10">
        <Command className="text-black">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-black/10">
            <Search className="w-5 h-5 text-neutral-500" />
            <Command.Input
              value={q}
              onValueChange={setQ}
              autoFocus
              placeholder="Busque serviços, cases, insights..."
              data-testid="command-input"
              className="flex-1 bg-transparent outline-none text-base placeholder:text-neutral-400"
            />
            <kbd className="text-[10px] bg-black/5 px-2 py-1 rounded">ESC</kbd>
          </div>
          <Command.List className="max-h-[60vh] overflow-y-auto p-2">
            <Command.Empty className="px-4 py-6 text-sm text-neutral-500">Nenhum resultado.</Command.Empty>
            <Command.Group heading="Páginas" className="text-xs text-neutral-500 px-3 pt-3">
              {NAV.map((n) => (
                <Command.Item key={n.key} value={`pagina ${n.key}`} onSelect={() => go(n.path)} className="flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer aria-selected:bg-black aria-selected:text-white">
                  <Layers className="w-4 h-4" /> <span className="capitalize">{n.key}</span>
                </Command.Item>
              ))}
            </Command.Group>
            <Command.Group heading="Serviços" className="text-xs text-neutral-500 px-3 pt-3">
              {SERVICES.map((s) => (
                <Command.Item key={s.slug} value={`servico ${s.title} ${s.benefit}`} onSelect={() => go(`/servicos#${s.slug}`)} className="flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer aria-selected:bg-black aria-selected:text-white">
                  <Briefcase className="w-4 h-4" /> <span>{s.title}</span> <span className="ml-auto text-xs text-neutral-400">{s.benefit}</span>
                </Command.Item>
              ))}
            </Command.Group>
            <Command.Group heading="Cases" className="text-xs text-neutral-500 px-3 pt-3">
              {CASES.map((c) => (
                <Command.Item key={c.slug} value={`case ${c.title} ${c.developer} ${(c.categories || []).join(" ")}`} onSelect={() => go(`/cases/${c.slug}`)} className="flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer aria-selected:bg-black aria-selected:text-white">
                  <FileText className="w-4 h-4" /> <span>{c.title}</span> <span className="ml-auto text-xs text-neutral-400">{c.developer}</span>
                </Command.Item>
              ))}
            </Command.Group>
            <Command.Group heading="Insights" className="text-xs text-neutral-500 px-3 pt-3">
              {INSIGHTS.map((i) => (
                <Command.Item key={i.slug} value={`insight ${i.title} ${i.topic} ${i.author}`} onSelect={() => go(`/insights/${i.slug}`)} className="flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer aria-selected:bg-black aria-selected:text-white">
                  <Lightbulb className="w-4 h-4" /> <span className="truncate">{i.title}</span>
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
